import { Badge, Divider, ListSubheader, Tooltip } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  CALENDAR_MANAGER_ROLES,
  PATIENT_RECORD_STAFF_ROLES,
  Role,
} from "./components/Access/Role";
import { useCerberus } from "./hooks/useCerberus";
import { useAudience } from "./components/Access/useAudience";
import { prefetchNavDestination } from "./navPrefetch";
import { useModuleEnabled } from "./modules/ModulesProvider";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CameraEnhanceIcon from "@mui/icons-material/CameraEnhance";
// Aliased: MUI's `Badge` component is imported above, and this is the icon of the same name.
// Not Group/People for Users — those two are aliases of one glyph in the Material set, so
// Patients and Users were rendering the exact same artwork. An ID badge reads as "accounts".
import BadgeIcon from "@mui/icons-material/Badge";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DisplayForRoles from "./components/Access/DisplayForRoles";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import MedicationIcon from "@mui/icons-material/Medication";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PeopleIcon from "@mui/icons-material/People";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import HistoryIcon from "@mui/icons-material/History";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ChatIcon from "@mui/icons-material/Chat";
import PaymentIcon from "@mui/icons-material/Payment";
import HelpIcon from "@mui/icons-material/Help";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { isSignupBlocked } from "./config/runtimeConfig";

export function NavigationListItems(props: {
  pathname: string;
  leftNavOpen: boolean;
}) {
  const { pathname, leftNavOpen } = props;
  const {
    unreadMessagesCount,
    notificationCount,
    userId,
    roles,
    userHasRole,
    userHasOneOfRoles,
  } = useCerberus();
  const { audience } = useAudience();

  // Per-deployment capability gates (OFF for CerebraLinked, ON for TeleHeadache).
  const providerCalendarEnabled = useModuleEnabled("provider-calendar");
  const billingEnabled = useModuleEnabled("billing");
  const patientVisitsEnabled = useModuleEnabled("patient-visits");
  const medicationRefillsEnabled = useModuleEnabled("medication-refills");
  const externalProvidersEnabled = useModuleEnabled("external-providers");
  // Physical clinics — memory-care only; TeleHeadache is telehealth. (Imaging centers are shared.)
  const clinicLocationsEnabled = useModuleEnabled("clinic-locations");
  const caregiversEnabled = useModuleEnabled("caregivers");

  // Define clinical roles (all roles except Patient and ExternalProvider)
  const clinicalRoles = [
    Role.Provider,
    Role.Admin,
    Role.Developer,
    Role.Nurse,
    Role.MedicalAssistant,
    Role.AuthTeam,
    Role.PacTeam,
    Role.MtcDoc,
  ];

  const isSelected = (path: string) => {
    return pathname.startsWith(path);
  };

  const NavItem = ({
    to,
    icon,
    label,
    badge = null,
  }: {
    to: string;
    icon: React.ReactNode;
    label: string;
    badge?: number | null;
  }) => {
    const selected = isSelected(to);
    const hasBadge = !!badge && badge > 0;
    // Hovering (or tabbing to) a link is dead time — spend it fetching the destination's chunk
    // and data so the click has nothing left to wait for. Focus is covered too, so keyboard
    // navigation gets the same head start.
    const warm = () => prefetchNavDestination(to, audience, { userId, roles });
    return (
      <ListItemButton
        selected={selected}
        component={RouterLink}
        to={to}
        onMouseEnter={warm}
        onFocus={warm}
        aria-current={selected ? "page" : undefined}
        // Explicit name so screen readers get the label + unread count even when
        // the drawer is collapsed (text hidden) and the badge is purely visual.
        aria-label={hasBadge ? `${label}, ${badge} unread` : label}
        sx={{
          minHeight: 48,
          justifyContent: leftNavOpen ? "flex-start" : "center",
          px: leftNavOpen ? 2 : 1.5,
          py: 1,
          mx: leftNavOpen ? 1 : 0.5,
          borderRadius: 1,
          mb: 0.5,
        }}
      >
        <Tooltip
          title={label}
          placement="right"
          arrow
          disableHoverListener={leftNavOpen ? true : false}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: leftNavOpen ? 2 : 0,
              justifyContent: "center",
              color: "inherit",
            }}
          >
            {hasBadge ? (
              <Badge
                color="error"
                // Full count when expanded; a dot when collapsed so a 3-digit
                // count can't overflow the narrow rail.
                variant={leftNavOpen ? "standard" : "dot"}
                badgeContent={badge}
                max={99}
              >
                {icon}
              </Badge>
            ) : (
              icon
            )}
          </ListItemIcon>
        </Tooltip>
        {leftNavOpen && (
          <ListItemText
            primary={label}
            sx={{
              color: "inherit",
              "& .MuiListItemText-primary": {
                fontSize: "0.875rem",
                fontWeight: 500,
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            }}
          />
        )}
      </ListItemButton>
    );
  };

  return (
    <>
      <NavItem to="/dashboard" icon={<DashboardIcon />} label="Dashboard" />

      {/* Patients before Calendar: it is the highest-frequency destination for most staff. */}
      {/* Internal staff: only the roles PatientsController admits (PATIENT_RECORD_STAFF_ROLES). */}
      {(userHasOneOfRoles(PATIENT_RECORD_STAFF_ROLES) ||
        userHasRole(Role.ExternalProvider) ||
        userHasRole(Role.ExternalOrgUser)) && (
        <NavItem to="/patients" icon={<PeopleIcon />} label="Patients" />
      )}

      {providerCalendarEnabled && userHasOneOfRoles(CALENDAR_MANAGER_ROLES) && (
        <NavItem to="/calendar" icon={<CalendarTodayIcon />} label="Calendar" />
      )}

      <NavItem
        to="/messages"
        icon={<ChatIcon />}
        label="Messages"
        badge={
          unreadMessagesCount && unreadMessagesCount > 0
            ? unreadMessagesCount
            : undefined
        }
      />

      {(userHasOneOfRoles(clinicalRoles) ||
        userHasRole(Role.ExternalProvider) ||
        userHasRole(Role.ExternalOrgUser) ||
        userHasRole(Role.Patient)) && (
        <NavItem
          to="/notifications"
          icon={<NotificationsActiveIcon />}
          label="Notifications"
          badge={
            notificationCount && notificationCount > 0
              ? notificationCount
              : undefined
          }
        />
      )}

      {/* Module-gated patient destinations. These pages existed and were correctly gated at the
          route, but had no nav entry at all — reachable only by returning to the dashboard and
          finding the right card, which made them dead ends once a patient navigated away.
          Scheduling is deliberately NOT here: it is an action that belongs on the Appointments
          page, not a place. */}
      {userHasRole(Role.Patient) && patientVisitsEnabled && (
        <NavItem
          to="/patient-appointments"
          icon={<EventAvailableIcon />}
          label="Appointments"
        />
      )}

      {userHasRole(Role.Patient) && medicationRefillsEnabled && (
        <NavItem
          to="/medication-refills"
          icon={<MedicationIcon />}
          label="Medication Refills"
        />
      )}

      {userHasRole(Role.Patient) && (
        <NavItem
          to="/medical-information"
          icon={<MedicalServicesIcon />}
          label="Medical Information"
        />
      )}

      {userHasRole(Role.Patient) && (
        <NavItem to="/help" icon={<HelpIcon />} label="Help" />
      )}

      {userHasOneOfRoles(clinicalRoles) && (
        <>
          {/* Visually separate the administration/tools group from the primary items. */}
          <Divider sx={{ my: 1 }} />
          {leftNavOpen && (
            <ListSubheader
              component="div"
              disableSticky
              sx={{
                bgcolor: "transparent",
                lineHeight: "2rem",
                fontSize: "0.7rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Administration
            </ListSubheader>
          )}
          {clinicLocationsEnabled && (
            <NavItem to="/locations" icon={<BusinessIcon />} label="Locations" />
          )}

          <NavItem
            to="/imaging-centers"
            icon={<CameraEnhanceIcon />}
            label="Imaging Centers"
          />

          <NavItem to="/users" icon={<BadgeIcon />} label="Users" />

          {/* Pre-launch only, like its dashboard card: the people who asked to hear when we open.
              Same roles as the route and GET /launch-waitlist. */}
          {isSignupBlocked() && (
            <DisplayForRoles roles={[Role.Admin, Role.Developer]}>
              <NavItem
                to="/launch-waitlist"
                icon={<MarkEmailReadOutlinedIcon />}
                label="Launch Waitlist"
              />
            </DisplayForRoles>
          )}

          {externalProvidersEnabled && (
            <DisplayForRoles roles={[Role.Admin]}>
              <NavItem
                to="/external"
                icon={<HandshakeIcon />}
                label="External Provider Interface"
              />
            </DisplayForRoles>
          )}

          {caregiversEnabled && (
            <DisplayForRoles roles={[Role.Admin]}>
              <NavItem
                to="/caregiver-access"
                icon={<SupervisorAccountIcon />}
                label="Caregiver Access"
              />
            </DisplayForRoles>
          )}

          {/* Billing lives here rather than among the clinical items: it is back-office work,
              so the primary list stays purely patient-facing. */}
          {billingEnabled && (
            <NavItem to="/billing" icon={<PaymentIcon />} label="Billing" />
          )}

          <DisplayForRoles roles={[Role.Developer]}>
            <NavItem
              to="/site-settings"
              icon={<SettingsOutlinedIcon />}
              label="Site Settings"
            />
          </DisplayForRoles>

          {/* Developer-only: the /audit route requires Developer, so gating the nav
              to Developer keeps Admins from clicking through to a 404. */}
          <DisplayForRoles roles={[Role.Developer]}>
            <NavItem to="/audit" icon={<HistoryIcon />} label="Logs" />
          </DisplayForRoles>

          {/* Developer-only, same reasoning as Logs: the route and the API both require it. */}
          <DisplayForRoles roles={[Role.Developer]}>
            <NavItem to="/dev/insights" icon={<InsightsOutlinedIcon />} label="Insights" />
          </DisplayForRoles>
        </>
      )}

      {/* Last, and for every audience: a personal utility rather than a daily task, and the
          avatar menu already offers it. It used to sit mid-list among the clinical items. */}
      <Divider sx={{ my: 1 }} />
      <NavItem to="/account" icon={<ManageAccountsIcon />} label="My Account" />
    </>
  );
}
